import { motion } from "framer-motion";
import { FlaskConical, Wrench, Scaling, Globe, Gauge, ShieldCheck } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const rowReveal = {
  hidden: { opacity: 0, x: -30 },
  visible: (i) => ({
    opacity: 1, x: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const reasons = [
  { icon: FlaskConical, num: "01", title: "Proprietary Formulations", desc: "India-focused syrup formulations tailored to local taste preferences with custom flavour development capabilities.", highlight: "20+ Unique Flavours" },
  { icon: Globe, num: "02", title: "Proven Global Technology", desc: "Backed by 40+ years of Australian heritage in high-quality beverage dispensing solutions exported worldwide.", highlight: "40+ Years Legacy" },
  { icon: Wrench, num: "03", title: "Complete Service Network", desc: "End-to-end service and maintenance support ensuring maximum machine uptime and operational efficiency.", highlight: "Pan-India Support" },
  { icon: Scaling, num: "04", title: "Scalable Model", desc: "From a single location to a nationwide network, our model scales seamlessly with your business growth.", highlight: "Unlimited Scale" },
  { icon: Gauge, num: "05", title: "High Throughput Machines", desc: "Industrial-grade dispensing systems designed for high-footfall environments with rapid service capability.", highlight: "Enterprise Grade" },
  { icon: ShieldCheck, num: "06", title: "Quality Assurance", desc: "Rigorous quality standards from formulation to dispensing, ensuring premium taste every single time.", highlight: "Zero Compromise" },
];

export default function WhyChooseSection() {
  return (
    <section id="why-us" data-testid="why-us-section" className="py-16 lg:py-28 bg-[#0B1D3A] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }}
      />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#C8D616]/5 blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-blue-500/5 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
          className="max-w-3xl mb-16"
        >
          <motion.p variants={fadeUp} className="text-[#C8D616] font-semibold text-sm uppercase tracking-widest mb-3">Why Choose Us</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-6">The Ultimate Infusions Advantage</motion.h2>
          <motion.p variants={fadeUp} className="text-base lg:text-lg text-slate-400 leading-relaxed">
            We combine global expertise with local innovation to deliver unmatched value in the Indian frozen and carbonated beverage market.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="space-y-4"
        >
          {reasons.map((item, i) => (
            <motion.div
              key={item.title}
              variants={rowReveal}
              custom={i}
              className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.03] hover:bg-white/[0.07] hover:border-[#C8D616]/20 transition-all duration-500 overflow-hidden"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 p-6 lg:p-8">
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-3xl lg:text-4xl font-extrabold text-white/[0.06] group-hover:text-[#C8D616]/15 transition-colors duration-500 select-none w-12 lg:w-14">{item.num}</span>
                  <div className="w-12 h-12 rounded-xl bg-[#C8D616]/10 flex items-center justify-center group-hover:bg-[#C8D616]/20 transition-colors duration-500">
                    <item.icon size={22} className="text-[#C8D616]" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
                <div className="shrink-0">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-[#C8D616]/10 text-[#C8D616] text-xs font-bold uppercase tracking-wider border border-[#C8D616]/20">{item.highlight}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
